import * as React from "react";
import { Button, ButtonGroup, Box, Typography } from "@mui/material";
import { sortBy, isNil } from "lodash";
import type { ClientFlow } from "./types";
import type { selectedYear } from "../../store/features/envSlice";

const selectableVariant = (isSelected: boolean) => (isSelected ? "contained" : "outlined");

export interface UniqueButtonProps {
  array: string[];
  value: string;
  setValue: (value: string) => void;
}

export const GenerateUniqueButtons: React.FC<UniqueButtonProps> = ({ array, value, setValue }) => {
  return (
    <ButtonGroup>
      {array.map((s) => {
        const isSelected = s === value;
        return (
          <Button key={s} onClick={() => setValue(isSelected ? "" : s)} variant={selectableVariant(isSelected)}>
            {s}
          </Button>
        );
      })}
    </ButtonGroup>
  );
};

export interface MultipleButtonProps {
  array: ClientFlow[];
  setArray: (array: ClientFlow[]) => void;
}

export const GenerateMultipleButtons: React.FC<MultipleButtonProps> = ({ array, setArray }) => {
  const [isFull, setIsFull] = React.useState<boolean>(false);

  React.useEffect(() => {
    const selected = isFull;
    setArray(array.map((client) => ({ ...client, selected })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFull]);

  React.useEffect(() => {
    if (array.filter((client) => client.selected).length === 0) {
      setIsFull(false);
    }
  }, [array]);

  return (
    <Box sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 1 }}>
      <Button onClick={() => setIsFull(!isFull)} variant="outlined">
        {isFull ? "reset" : "full"}
      </Button>
      {sortBy(array, ["id"]).map((c) => (
        <Button
          key={c.id}
          onClick={() => setArray(array.map((s) => (c === s ? { ...s, selected: !s.selected } : s)))}
          variant={selectableVariant(c.selected)}
        >
          {c.id} - {c.name}
        </Button>
      ))}
    </Box>
  );
};

export interface MultipleRowButtonProps {
  array: ClientFlow[];
  value: selectedYear[];
  setValue: (value: selectedYear[]) => void;
}

export const GenerateMultipleRowButtons: React.FC<MultipleRowButtonProps> = ({ array: allValues, value: values, setValue: setValues }) => {
  const areEqual = (item0: selectedYear, item1: selectedYear) => item0.id === item1.id && item0.year === item1.year;
  const isSelected = (item: selectedYear) => !isNil(values.find((_item) => areEqual(item, _item)));

  const handleButtonClick = (item: selectedYear) => {
    if (isSelected(item)) {
      setValues(values.filter((_item) => !areEqual(item, _item)));
    } else {
      setValues([...values, item]);
    }
  };

  return (
    <>
      {allValues
        .filter((c) => c.selected)
        .map((c) => (
          <Box key={c.id} sx={{ my: 1 }}>
            <Typography variant="h5">{c.name}</Typography>
            <ButtonGroup aria-label="outlined primary button group">
              {c.allYears.map((y) => {
                const item: selectedYear = { id: c.id, year: parseInt(y, 10) } as selectedYear;
                const key = `${c.id}-${y}`;
                return (
                  <Button key={key} onClick={() => handleButtonClick(item)} variant={selectableVariant(isSelected(item))}>
                    {y}
                  </Button>
                );
              })}
            </ButtonGroup>
          </Box>
        ))}
    </>
  );
};